import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, Users, IndianRupee, AlertTriangle, MessageSquare, Calendar, FileX,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, LineChart, Line, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const ACTION_PAGE_SIZE = 10;

const PROGRAM_COLORS = ["#0F172A", "#4D7C0F", "#F59E0B", "#3B82F6"];

function StatCard({ icon: Icon, label, value, accent = "slate", testid }) {
  const accentMap = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    red: "bg-red-100 text-red-700",
    lime: "bg-lime-100 text-lime-700",
    blue: "bg-blue-100 text-blue-700",
  };
  return (
    <div className="stat-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-caps mb-3">{label}</div>
          <div className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>{value}</div>
        </div>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${accentMap[accent]}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overduePage, setOverduePage] = useState(1);
  const [docsPage, setDocsPage] = useState(1);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats");
      setData(data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendQuickReminder = async (studentId) => {
    try {
      await api.post("/reminders/send", { student_ids: [studentId], template_type: "overdue" });
      toast.success("Reminder sent");
      load();
    } catch {
      toast.error("Failed to send reminder");
    }
  };

  if (loading || !data) {
    return <div className="p-8 text-slate-500">Loading dashboard...</div>;
  }

  const { summary, program_split, monthly_trend, overdue_students, recent_admissions, documents_pending, pending_approvals } = data;

  const overdueSlice = overdue_students.slice((overduePage - 1) * ACTION_PAGE_SIZE, overduePage * ACTION_PAGE_SIZE);
  const docsSlice = documents_pending.slice((docsPage - 1) * ACTION_PAGE_SIZE, docsPage * ACTION_PAGE_SIZE);

  return (
    <div className="p-6 md:p-10 max-w-[1400px] mx-auto" data-testid="admin-dashboard">
      <div className="mb-8">
        <div className="label-caps mb-2">Overview</div>
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Dashboard</h1>
        <p className="text-slate-500 mt-1">Real-time snapshot of admissions, fees and reminders.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        <StatCard icon={Users} label="Total Students" value={summary.total_students} accent="lime" testid="stat-total-students" />
        <StatCard icon={Calendar} label="Students this year" value={summary.students_this_year} accent="slate" testid="stat-students-year" />
        <div className="stat-card" data-testid="stat-fees-today">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="label-caps mb-3">Fees Collected (Today)</div>
              <div className="text-3xl font-bold tracking-tight text-emerald-700" style={{ fontFamily: "Chivo" }}>
                {formatINR(summary.fees_today || 0)}
              </div>
              <div className="text-xs text-slate-500 mt-2">Payments received since midnight (IST/UTC).</div>
            </div>
            <div className="w-10 h-10 rounded-md flex items-center justify-center bg-emerald-100 text-emerald-700">
              <IndianRupee size={20} />
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-fees-month">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="label-caps mb-3">Fees Collected (Month)</div>
              <div className="text-3xl font-bold tracking-tight text-emerald-700" style={{ fontFamily: "Chivo" }}>
                {formatINR(summary.net_fees_this_month ?? summary.fees_this_month)}
              </div>
              <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                <div>Gross: <span className="font-semibold text-slate-700">{formatINR(summary.fees_this_month)}</span></div>
                <div>Expenses: <span className="font-semibold text-red-600">− {formatINR(summary.expenses_this_month || 0)}</span></div>
              </div>
            </div>
            <div className="w-10 h-10 rounded-md flex items-center justify-center bg-emerald-100 text-emerald-700">
              <IndianRupee size={20} />
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-fees-all-time">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="label-caps mb-3">Fees Collected (All Time)</div>
              <div className="text-3xl font-bold tracking-tight text-emerald-700" style={{ fontFamily: "Chivo" }}>
                {formatINR(summary.net_fees_all_time ?? summary.fees_all_time ?? 0)}
              </div>
              <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                <div>Gross: <span className="font-semibold text-slate-700">{formatINR(summary.fees_all_time || 0)}</span></div>
                <div>Expenses: <span className="font-semibold text-red-600">− {formatINR(summary.expenses_all_time || 0)}</span></div>
              </div>
            </div>
            <div className="w-10 h-10 rounded-md flex items-center justify-center bg-lime-100 text-lime-700">
              <IndianRupee size={20} />
            </div>
          </div>
        </div>
        <StatCard icon={AlertTriangle} label="Overdue amount" value={formatINR(summary.overdue_amount)} accent="red" testid="stat-overdue" />
      </div>

      {pending_approvals && pending_approvals.length > 0 && (
        <Card className="p-6 mb-6 border-amber-300 bg-amber-50" data-testid="pending-approvals-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps text-amber-800">Action Required</div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Chivo" }}>
                {pending_approvals.length} Pending Registration{pending_approvals.length > 1 ? "s" : ""}
              </h3>
            </div>
            <Badge variant="outline" className="status-partial">{pending_approvals.length}</Badge>
          </div>
          <div className="divide-y divide-amber-200">
            {pending_approvals.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/students/${s.id}`)}
                className="w-full py-3 flex items-center justify-between text-left hover:bg-amber-100 -mx-2 px-2 rounded"
                data-testid={`pending-${s.id}`}
              >
                <div>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-slate-600 font-mono">{s.student_id} · {s.phone} · {s.program}</div>
                </div>
                <Badge variant="outline" className="status-partial">Review →</Badge>
              </button>
            ))}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Monthly trend */}
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps">Last 6 months</div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Chivo" }}>Enrollments & Fee Collection</h3>
            </div>
            <TrendingUp size={20} className="text-slate-400" />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthly_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
              <YAxis yAxisId="left" stroke="#0F172A" fontSize={11} />
              <YAxis yAxisId="right" orientation="right" stroke="#4D7C0F" fontSize={11} />
              <Tooltip />
              <Line yAxisId="left" type="monotone" dataKey="enrollments" stroke="#0F172A" strokeWidth={2} name="Enrollments" />
              <Line yAxisId="right" type="monotone" dataKey="fees" stroke="#4D7C0F" strokeWidth={2} name="Fees ₹" />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Program split */}
        <Card className="p-6">
          <div className="label-caps mb-1">Program Split</div>
          <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: "Chivo" }}>By enrollment</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={program_split} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                {program_split.map((_, i) => (
                  <Cell key={i} fill={PROGRAM_COLORS[i % PROGRAM_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1 text-sm">
            {program_split.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ background: PROGRAM_COLORS[i % PROGRAM_COLORS.length] }} />
                  <span className="text-slate-700">{p.name}</span>
                </div>
                <span className="font-semibold">{p.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Action lists */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6" data-testid="overdue-list">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps">Action Required</div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Chivo" }}>Overdue Students</h3>
            </div>
            <Badge variant="outline" className="status-overdue">{overdue_students.length}</Badge>
          </div>
          {overdue_students.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No overdue students 🎉</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {overdueSlice.map((s) => (
                <div key={s.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate">{s.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{s.student_id} · {s.program}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-red-700">{formatINR(s.outstanding)}</div>
                  </div>
                  <Button
                    size="sm"
                    data-testid={`send-reminder-${s.id}`}
                    onClick={() => sendQuickReminder(s.id)}
                    className="bg-slate-900 hover:bg-slate-800 h-8 text-xs"
                  >
                    <MessageSquare size={12} className="mr-1" /> Remind
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Pagination page={overduePage} pageSize={ACTION_PAGE_SIZE} total={overdue_students.length} onPageChange={setOverduePage} testIdPrefix="overdue-pagination" />
        </Card>

        <Card className="p-6" data-testid="recent-admissions">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps">Last 10</div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Chivo" }}>Recent Admissions</h3>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate("/students")} data-testid="view-all-students">
              View all
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {recent_admissions.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/students/${s.id}`)}
                className="w-full py-3 flex items-center justify-between gap-3 text-left hover:bg-slate-50 -mx-2 px-2 rounded"
                data-testid={`recent-student-${s.id}`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{s.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{s.student_id} · {s.program}</div>
                </div>
                <div className="text-xs text-slate-500">{formatDate(s.enrollment_date)}</div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-6">
          <div className="label-caps mb-1">Documents</div>
          <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: "Chivo" }}>Pending Submissions</h3>
          {documents_pending.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">All documents collected ✓</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {docsSlice.map((s) => (
                <div key={s.id} className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileX size={16} className="text-amber-600" />
                    <div>
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-slate-500">{s.missing.join(", ")}</div>
                    </div>
                  </div>
                  <Badge variant="outline" className="status-partial">{s.pending_count} missing</Badge>
                </div>
              ))}
            </div>
          )}
          <Pagination page={docsPage} pageSize={ACTION_PAGE_SIZE} total={documents_pending.length} onPageChange={setDocsPage} testIdPrefix="docs-pending-pagination" />
        </Card>

        <Card className="p-6">
          <div className="label-caps mb-1">Reminders</div>
          <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: "Chivo" }}>WhatsApp Activity</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-lime-50 rounded-md border border-lime-200">
              <div className="text-xs uppercase tracking-wider text-lime-800 font-semibold">Today</div>
              <div className="text-3xl font-bold text-lime-900 mt-1" style={{ fontFamily: "Chivo" }}>{summary.reminders_today}</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-700 font-semibold">This week</div>
              <div className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo" }}>{summary.reminders_week}</div>
            </div>
          </div>
          <Button
            className="w-full mt-4 bg-slate-900 hover:bg-slate-800"
            onClick={() => navigate("/reminders")}
            data-testid="goto-reminders"
          >
            <MessageSquare size={16} className="mr-2" /> Go to WhatsApp Reminders
          </Button>
        </Card>
      </div>
    </div>
  );
}
